import type { AppEnv, YoutubeMode } from '@qlk/shared';

/**
 * Dải nhãn cố định trên cùng mọi trang: đang làm việc trên **dữ liệu thật** hay
 * **dữ liệu thử**.
 *
 * App chạy song song hai database (`qlk_main` cho dev, `qlk_live_thu` cho kênh
 * thật) với giao diện giống nhau từng pixel. Không có dải này thì rất dễ bấm
 * "Xoá vĩnh viễn" trên dữ liệu thật trong khi nghĩ mình đang ở bản thử — đã xảy
 * ra một lần với kênh thật.
 *
 * Dải đỏ ở `live` và in **tên database** để phân biệt được cả hai DB live với
 * nhau. Dải xám ở `mock`: nhắc rằng số liệu là giả, không phải cảnh báo.
 *
 * **Trên máy chủ thật (`appEnv === 'production'`) thì dải `live` không hiện.**
 * Ở đó chỉ có một database duy nhất và không có bản thử nào để nhầm, nên dải
 * chỉ chiếm chỗ trên mọi trang. Chuyện nhầm hai DB là chuyện của máy dev, nơi
 * `npm run dev` (qlk_main) và `npm run dev:live` (qlk_live_thu) mở cùng lúc
 * trên hai tab trông giống nhau từng pixel — ở đó dải vẫn còn.
 *
 * Dải xám `mock` thì **vẫn hiện cả trên máy chủ thật**: máy chủ chạy dữ liệu
 * giả là tình trạng cần biết ngay, không phải chuyện bình thường.
 *
 * Cảnh báo trong hộp thoại việc khó lùi (`CanhBaoDuLieuThat` bên dưới) không
 * phụ thuộc `appEnv`: đó là chỗ cần nhắc nhất và chỉ hiện khi sắp bấm.
 */
export interface DaiCheDoDuLieuProps {
  youtubeMode: YoutubeMode;
  databaseName: string;
  /** `production` = máy chủ thật, xem ghi chú trên. Thiếu thì coi như máy dev. */
  appEnv?: AppEnv;
}

/**
 * Có hiện dải hay không. Tách ra vì `AppShell` phải biết **trước khi render**:
 * biến CSS `--cao-dai` là chiều cao dải, và thanh trên + sidebar dính theo nó.
 * Ẩn dải mà vẫn để `--cao-dai: 32px` thì đầu trang chừa một khoảng trống 32px.
 */
export function hienDaiCheDo(youtubeMode: YoutubeMode, appEnv?: AppEnv): boolean {
  return !(youtubeMode === 'live' && appEnv === 'production');
}

export function DaiCheDoDuLieu({ youtubeMode, databaseName, appEnv }: DaiCheDoDuLieuProps) {
  const laThat = youtubeMode === 'live';

  if (!hienDaiCheDo(youtubeMode, appEnv)) return null;

  return (
    <div
      role="status"
      aria-label={laThat ? 'Đang dùng dữ liệu thật' : 'Đang dùng dữ liệu thử'}
      className={`sticky top-0 z-40 flex h-(--cao-dai) items-center justify-center gap-x-2 px-4 text-center text-phu font-semibold tracking-wide ${
        laThat ? 'bg-bad-chu text-white' : 'bg-neutral-chu text-white'
      }`}
    >
      {laThat ? (
        <>
          <span>DỮ LIỆU THẬT</span>
          <span aria-hidden="true">·</span>
          <span className="so-lieu font-bold">{databaseName}</span>
        </>
      ) : (
        <span>DỮ LIỆU THỬ</span>
      )}
    </div>
  );
}

/**
 * Cảnh báo đỏ trong hộp thoại việc khó lùi (lưu trữ, xoá vĩnh viễn) khi đang ở
 * chế độ `live`. Ở `mock` không hiện gì.
 */
export function CanhBaoDuLieuThat({ youtubeMode }: { youtubeMode: YoutubeMode | undefined }) {
  if (youtubeMode !== 'live') return null;

  return (
    <p role="alert" className="rounded-[10px] border border-bad-chu bg-bad-nen p-3 text-bad-chu">
      <strong>Bạn đang thao tác trên dữ liệu thật.</strong> Đây là database của kênh YouTube thật,
      không phải bản thử.
    </p>
  );
}
